import type { GroupField } from "./methods";

export const ADDRESS_TAG = "group_address";
export const PAYMENT_TAG = "payment";

export const BILLING_ADDRESS_FIELD_KEY_BY_TAG = {
  full_name: "contact.std_billing_address_full_name",
  phone: "contact.std_billing_address_phone",
  address: "contact.std_billing_address_address1",
  country: "contact.std_billing_address_country",
  state: "contact.std_billing_address_state",
  postal_code: "contact.std_billing_address_postal_code",
  city: "contact.std_billing_address_city",
} as const;

const BILLING_ADDRESS_FIELD_KEY_SET = new Set<string>(
  Object.values(BILLING_ADDRESS_FIELD_KEY_BY_TAG)
);

export interface SurveyAddressSlide {
  id?: string | number;
  slideName?: string | number;
  slideData: GroupField[];
}

export interface FieldLocation {
  field: GroupField;
  slideIndex: number;
  elementIndex: number;
}

/** Treat form fields as one slide so the same Billing operations work for both builders. */
export const asSlides = (fields: GroupField[]): SurveyAddressSlide[] => [
  { slideData: fields },
];

const findField = (
  slides: SurveyAddressSlide[],
  predicate: (field: GroupField) => boolean
): FieldLocation | undefined => {
  for (let slideIndex = 0; slideIndex < slides.length; slideIndex += 1) {
    const slideData = slides[slideIndex]?.slideData || [];
    const elementIndex = slideData.findIndex(
      (field) => field !== undefined && predicate(field)
    );
    if (elementIndex !== -1) {
      return { field: slideData[elementIndex], slideIndex, elementIndex };
    }
  }
  return undefined;
};

export const findAddress = (
  slides: SurveyAddressSlide[]
): FieldLocation | undefined =>
  findField(slides, (field) => field.tag === ADDRESS_TAG);

export const hasGroupAddress = (slides: SurveyAddressSlide[]): boolean =>
  Boolean(findAddress(slides));

export const isStandaloneBillingAddressField = (
  field: Pick<GroupField, "fieldKey"> | undefined
): boolean =>
  typeof field?.fieldKey === "string" &&
  BILLING_ADDRESS_FIELD_KEY_SET.has(field.fieldKey);

/** Keep billing off when its source Address element has been deleted. */
export const clearBillingWithoutAddress = (
  slides: SurveyAddressSlide[]
): boolean => {
  if (hasGroupAddress(slides)) return false;
  let changed = false;
  slides.forEach((slide) => {
    slide.slideData.forEach((field) => {
      if (field.tag === PAYMENT_TAG && field.billingAddressEnabled === true) {
        field.billingAddressEnabled = false;
        changed = true;
      }
    });
  });
  return changed;
};

/** Enable the Billing projection only when a group Address supplies its fields. */
export const enableBillingAddress = ({
  slides,
  paymentSlideIndex,
  paymentIndex,
}: {
  slides: SurveyAddressSlide[];
  paymentSlideIndex: number;
  paymentIndex: number;
}): FieldLocation | undefined => {
  if (!hasGroupAddress(slides)) return undefined;
  const preferred = slides[paymentSlideIndex]?.slideData?.[paymentIndex];
  const payment =
    preferred?.tag === PAYMENT_TAG
      ? {
          field: preferred,
          slideIndex: paymentSlideIndex,
          elementIndex: paymentIndex,
        }
      : findField(slides, (field) => field.tag === PAYMENT_TAG);
  if (!payment) return undefined;

  slides.forEach((slide) => {
    const slideData = slide?.slideData || [];
    for (let index = slideData.length - 1; index >= 0; index -= 1) {
      if (isStandaloneBillingAddressField(slideData[index])) {
        slideData.splice(index, 1);
      }
    }
  });
  payment.field.billingAddressEnabled = true;
  return findField(slides, (field) => field === payment.field);
};
